import { Component, ComponentFactoryResolver, OnDestroy, OnInit } from '@angular/core';
import { RADIO_PLAN_DESC } from '@app/core/models/Radio';
import { RadioWatcherService } from '@app/core/ngxs/radio-watcher.service';
import { AppState } from '@app/core/states/app.state';
import { StopAudio } from '@app/core/states/audio.actions';
import { AudioState } from '@app/core/states/audio.state';
import { LiveTrackingState } from '@app/core/states/live-tracking.state';
import { RadioState } from '@app/core/states/radio.state';
import { ShoutOutsRequest } from '@app/core/states/shout-outs.actions';
import { LibraryCreatePlaylistComponent } from '@app/library/components/sides/library-create-playlist/library-create-playlist.component';
import { LibraryEditPlaylistComponent } from '@app/library/components/sides/library-edit-playlist/library-edit-playlist.component';
import { LibraryFilterPanelComponent } from '@app/library/components/sides/library-filter-panel/library-filter-panel.component';
import { LibraryTrackEditComponent } from '@app/library/components/sides/library-track-edit/library-track-edit.component';
import { PlaylistAddTrackComponent } from '@app/library/components/sides/playlist-add-track/playlist-add-track.component';
import { TagEditComponent } from '@app/library/components/sides/tag-edit/tag-edit.component';
import { TrackDetailsComponent } from '@app/library/components/sides/track-details/track-details.component';
import { librarySideToasts, libraryToasts } from '@app/library/library.toasts';
import { Playlist } from '@app/library/models/playlist.model';
import { LibraryModalService } from '@app/library/services/library-modal.service';
import { EditingTrackState } from '@app/library/states/editing-track.state';
import {
  AddToPlaylistWaitDuplicates,
  PlaylistsRequest,
  ResumePlaylistProcess,
} from '@app/library/states/playlists.actions';
import { PlaylistsState } from '@app/library/states/playlists.state';
import { TagsListRequest } from '@app/library/states/radio-tags.actions';
import { AllTracksForPlaylistRequest } from '@app/library/states/tracks.actions';
import { ArchivedTracksRequest } from '@app/library/states/trash.actions';
import { planBigger } from '@app/shared/utils';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { ToasterService } from '@radioking/shared/common-services';
import {
  PanelContainerInput,
  PanelRegistryService,
  PANELS_KEYS,
} from '@radioking/shared/pannel-manager';
import amplitude from 'amplitude-js';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { BacGroup } from '../../../models/bac.model';
import { BacsRequest } from '../../../states/bacs.actions';
import { BacsState } from '../../../states/bacs.state';

@Component({
  selector: 'rk-library-shell',
  templateUrl: './library-shell.component.html',
  styleUrls: ['./library-shell.component.scss'],
})
export class LibraryShellComponent implements OnInit, OnDestroy {
  sides: PanelContainerInput[] = [
    {
      key: PANELS_KEYS.CREATE_PLAYLIST,
      val: LibraryCreatePlaylistComponent,
    },
    {
      key: PANELS_KEYS.EDIT_TRACK_INFO,
      val: LibraryTrackEditComponent,
    },
    {
      key: PANELS_KEYS.EDIT_PLAYLIST,
      val: LibraryEditPlaylistComponent,
    },
    {
      key: PANELS_KEYS.FILTER_EDIT,
      val: LibraryFilterPanelComponent,
    },
    {
      key: PANELS_KEYS.ADD_TO_PLAYLIST,
      val: PlaylistAddTrackComponent,
    },
    {
      key: PANELS_KEYS.EDIT_TAG,
      val: TagEditComponent,
    },
    {
      key: PANELS_KEYS.TRACK_DETAILS,
      val: TrackDetailsComponent,
    },
  ];

  @Select(BacsState.bacsGroup)
  bacsGroups$: Observable<BacGroup[]>;

  @Select(BacsState.isLoading)
  isLoading$: Observable<boolean>;

  @Select(PlaylistsState.playlists)
  playlists$: Observable<Playlist[]>;

  @Select(PlaylistsState.isLoading)
  isLoadingPlaylist$: Observable<boolean>;

  @Select(EditingTrackState.isEditing)
  isEditing$: Observable<boolean>;

  @Select(RadioState.currentRadioId)
  currentRadioId$: Observable<number>;

  @Select(LiveTrackingState.planDesc)
  planDesc$: Observable<RADIO_PLAN_DESC>;

  subscription = new Subscription();

  constructor(
    private readonly watcher: RadioWatcherService,
    private readonly actions$: Actions,
    private readonly modalService: LibraryModalService,
    private readonly panelRegistry: PanelRegistryService,
    private readonly componentFactory: ComponentFactoryResolver,
    private readonly toaster: ToasterService,
    private readonly store: Store,
  ) {}

  ngOnInit() {
    amplitude.getInstance().logEvent('show library');
    this.toaster.registerToasts(libraryToasts);
    this.toaster.registerSideToasts(librarySideToasts);
    this.panelRegistry.registerMultiplePanels(this.sides, this.componentFactory);
    this.subscription.add(this.watcher.dispatchAndWatch(new BacsRequest()));
    this.subscription.add(
      this.watcher.dispatchAndWatch(new AllTracksForPlaylistRequest()),
    );
    this.subscription.add(this.watcher.dispatchAndWatch(new PlaylistsRequest()));
    this.subscription.add(this.watcher.dispatchAndWatch(new TagsListRequest(true)));
    this.subscription.add(this.watcher.dispatchAndWatch(new ArchivedTracksRequest()));

    this.planDesc$.pipe(filter(p => !!p)).subscribe(plan => {
      if (planBigger(plan, 'Demo')) {
        this.subscription.add(this.watcher.dispatchAndWatch(new ShoutOutsRequest()));
      }
    });
    // this.setupRouteWatcher();

    const waitAction = this.actions$
      .pipe(ofActionDispatched(AddToPlaylistWaitDuplicates))
      .subscribe(data => {
        this.modalService.addDuplicateToPlaylist(
          data.tracks,
          data.playlistId,
          data.position,
          window.innerWidth < 768,
        );
      });
    this.subscription.add(waitAction);
    this.resumePlaylistProcess();
  }

  @Dispatch()
  resumePlaylistProcess() {
    return new ResumePlaylistProcess();
  }

  ngOnDestroy(): void {
    this.panelRegistry.unregisterMultiplePanels(this.sides);
    this.subscription.unsubscribe();
    if (
      this.store.selectSnapshot(AppState.onMobile) &&
      !!this.store.selectSnapshot(AudioState.currentTrackPlaying)
    ) {
      this.store.dispatch(new StopAudio());
    }
  }

  protected readonly window = window;
}
